import UntitledIcon, { TIconName } from '@payaca/untitled-icons';
import React, { FunctionComponent, useMemo, useState, type JSX } from 'react';
import BasicField from '../basicField/BasicField';
import Button from '../button/Button';
import { ButtonStyleVariant } from '../button/enums';
import Modal from '../modal/Modal';

import './PersistedFileControl.sass';

type Props = {
  persistedFile: {
    fileName: string;
    identifier: any;
    thumbnailUrl?: string;
    mimeType?: string;
    downloadUrl?: string;
  };
  isDisabled?: boolean;
  allowFileNameModification?: boolean;
  showFileName?: boolean;
  isPersistingFileName?: boolean;
  isRemoving?: boolean;
  persistFileName?: (fileName: string) => void;
  removeFile?: (fileIdentifier: any) => void;
  canRemove?: boolean;
};

const PersistedFileControl: FunctionComponent<Props> = ({
  persistedFile,
  isDisabled = false,
  allowFileNameModification = false,
  showFileName = true,
  isPersistingFileName = false,
  isRemoving = false,
  persistFileName,
  removeFile,
  canRemove = false,
}: React.PropsWithChildren<Props>): JSX.Element => {
  const [isModifyingFileName, setIsModifyingFileName] = useState(false);
  const [fileName, setFileName] = useState(persistedFile.fileName);

  const handleSaveFileName = () => {
    if (persistFileName) {
      persistFileName(fileName);
    }
    setIsModifyingFileName(false);
  };

  const handleCloseModal = () => {
    setIsModifyingFileName(false);
  };

  const fileIcon: TIconName = useMemo(() => {
    switch (persistedFile.mimeType) {
      case 'application/pdf':
        return 'pdf';
      case 'image/png':
      case 'image/jpeg':
        return 'image-01.3';
      case 'text/csv':
        return 'file-csv';
      default:
        return 'file-04';
    }
  }, [persistedFile.mimeType]);

  return (
    <div
      className={`persisted-file-control file-block ${
        persistedFile.downloadUrl ? 'downloadable' : ''
      }}`}
    >
      <a
        href={persistedFile.downloadUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        <div
          className={`main-block ${
            !persistedFile.thumbnailUrl ? '' : 'image-block'
          }`}
        >
          {persistedFile.thumbnailUrl ? (
            <img src={persistedFile.thumbnailUrl} />
          ) : (
            <UntitledIcon name={fileIcon} />
          )}
        </div>
      </a>
      {showFileName && (
        <small>
          {persistedFile.fileName}{' '}
          {allowFileNameModification && persistFileName && !isDisabled && (
            <span
              className="modify-file-name-trigger"
              onClick={() => {
                setIsModifyingFileName(true);
              }}
            >
              <UntitledIcon
                name={isPersistingFileName ? 'loading-01.3' : 'edit-02.3'}
              />
            </span>
          )}
        </small>
      )}

      {!isDisabled && canRemove && !!removeFile && (
        <div
          className="remove-file-trigger"
          onClick={() => !isRemoving && removeFile?.(persistedFile.identifier)}
        >
          <UntitledIcon name={isRemoving ? 'loading-01.3' : 'x.3'} />
        </div>
      )}
      <Modal
        isOpen={isModifyingFileName}
        onClose={handleCloseModal}
        size="xs"
        actions={
          <Button
            styleVariant={ButtonStyleVariant.OUTSIZE}
            onClick={handleSaveFileName}
          >
            Save
          </Button>
        }
      >
        <BasicField
          name="fileName"
          value={fileName}
          onChange={(value: { [key: string]: any }) => {
            setFileName(value.fileName);
          }}
        />
      </Modal>
    </div>
  );
};

export default PersistedFileControl;
